import { useCallback } from "react";
import { ShopifyItemsTable } from "@/components/view/ShopifyItemsTable";
import type { ShopifySectionWidgetProps } from "../types";
import type { SalesOrderItemCustomization } from "@/lib/erp/types/order";

/**
 * Items Section Widget
 *
 * Displays the items table using ShopifyItemsTable component.
 * This is a special section that doesn't use the standard card layout.
 *
 * NOTE: Totals calculation is handled server-side via useDocCalculation hook
 * in SalesOrderDetailPage. This widget only updates the items field.
 */
export function ItemsSection({
  section,
  formData,
  onFieldChange,
  disabled,
  resetKey,
  viewMode,
  metaFields,
  isNew,
}: ShopifySectionWidgetProps) {
  // Find the items field
  const allFields = section.columns.flatMap((col: any) => col.fields);
  const itemsField = allFields.find(
    (f: any) => f.fieldtype === "Table" && f.fieldname === "items"
  );

  if (!itemsField) {
    return null;
  }

  // Guard: Check if metaFields is available
  if (!metaFields || metaFields.length === 0) {
    if (import.meta.env?.DEV) {
      console.warn(
        "[ItemsSection] Items section detected but metaFields is missing",
        {
          sectionName: section.name,
        }
      );
    }
    return null;
  }

  const itemsFieldSchema = metaFields.find(
    (f) => f.fieldname === itemsField.fieldname
  );

  if (!itemsFieldSchema) {
    if (import.meta.env?.DEV) {
      console.warn(
        "[ItemsSection] Items field found but schema not in metaFields",
        {
          fieldname: itemsField.fieldname,
          sectionName: section.name,
        }
      );
    }
    return null;
  }

  // Handle items change - only update items field
  // NOTE: Do NOT calculate totals client-side!
  // The parent component should use useDocCalculation hook for server-calculated totals
  const handleItemsChange = useCallback(
    (newItems: Record<string, unknown>[]) => {
      onFieldChange(itemsField.fieldname, newItems);
    },
    [onFieldChange, itemsField.fieldname]
  );

  // Handle customizations change — writes to item_customizations child table
  const handleCustomizationsChange = useCallback(
    (customizations: SalesOrderItemCustomization[]) => {
      onFieldChange("item_customizations", customizations);
    },
    [onFieldChange]
  );

  return (
    <div className="space-y-6 mb-6">
      <ShopifyItemsTable
        field={itemsFieldSchema}
        value={formData[itemsField.fieldname]}
        onChange={handleItemsChange}
        formData={formData}
        disabled={disabled}
        resetKey={resetKey}
        viewMode={viewMode}
        isNew={isNew}
        onCustomizationsChange={handleCustomizationsChange}
      />
    </div>
  );
}

